import { useState } from 'react'
import { diaLocal, fechaCorta, guaranies, hora } from '../datos/formato.js'
import { Boton } from '../comunes/Boton.js'

/**
 * La caja de recepción: apertura, libro del turno y cierre con arqueo (§6.1).
 *
 * **Excepción declarada a "la UI lee sólo de Dexie" (§5.5).** El arqueo es una
 * cuenta sobre lo que el servidor tiene registrado, no sobre lo que alcanzó a
 * llegar a este dispositivo: contar el cajón con datos a medio sincronizar es
 * peor que no contarlo. Cobrar sí sigue siendo offline —eso va por el outbox—;
 * abrir, arquear y cerrar necesitan la verdad completa.
 *
 * La distinción que ordena la pantalla: **medio de pago y destino son cosas
 * distintas**. La tarjeta pertenece al turno y se lista, pero no está en el
 * cajón. Por eso el número grande es sólo el efectivo, y los otros medios van
 * al costado.
 *
 * Desde T26 el turno lo levanta `Dinero`, que lo muestra en la cabecera
 * común a las dos solapas: acá se recibe por props, es el mismo dato, no dos
 * lecturas que se pueden pelear.
 */

const API = '/api/caja'

export interface Movimiento {
  id: string
  tipo: 'apertura' | 'pago' | 'retiro' | 'ingreso' | 'ajuste' | 'cierre'
  medio: string
  monto: number
  concepto: string | null
  createdAt: string
}

export interface Turno {
  id: string
  montoInicial: number
  esperado: number
  porMedio: Record<string, number>
  movimientos: Movimiento[]
}

/**
 * Un cobro a caja que entró sin turno abierto (T25). Lo típico: la recepción
 * cobró con la caja cerrada. Los del día se reclaman al abrir el turno; los de
 * días anteriores quedan acá, y hay que saberlo porque no se suman solos.
 */
export interface PendienteArqueo {
  id: string
  /** ISO 8601 en UTC: acá sólo se presenta (§6). */
  fecha: string
  monto: number
  medio: string
  clienteNombre: string
}

const ETIQUETA_TIPO: Record<Movimiento['tipo'], string> = {
  apertura: 'Apertura',
  pago: 'Cobro',
  retiro: 'Retiro',
  ingreso: 'Ingreso',
  ajuste: 'Ajuste',
  cierre: 'Cierre',
}

const ETIQUETA_MEDIO: Record<string, string> = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  otro: 'Otro',
}

export interface CajaProps {
  turno: Turno | null
  /** Los cobros a caja sin turno. Los levanta `Dinero` con el turno, para que
   *  la franja ámbar y esta lista nunca se contradigan. */
  pendientes: PendienteArqueo[]
  cargando: boolean
  error: string | null
  /** Recargar el turno desde el servidor: lo dispara quien abre, cierra o mueve la caja. */
  onCambio: () => void
  onError: (m: string | null) => void
}

export function Caja({ turno, pendientes, cargando, error, onCambio, onError }: CajaProps) {
  // Lo que avisó la apertura: «se sumaron N cobros de antes de abrir». Vive
  // acá, no en CajaCerrada, porque dejar de mostrar el turno cerrado no es el
  // momento de borrar la información: el dato interesa justo ahora que el
  // turno quedó abierto y hay que mirarlo.
  const [reclamados, setReclamados] = useState(0)

  if (cargando && turno === null) {
    return <p className="txt-meta">Consultando…</p>
  }

  return (
    <>
      {error && (
        <p className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {/* Los cobros a caja que todavía no entraron a ningún turno: van arriba
          del turno (abierto o cerrado) porque es lo que hay que mirar antes de
          cerrar. Los del día se reclaman al abrir; los de días anteriores
          quedan acá y hay que decir por qué. */}
      <PendientesDeArqueo pendientes={pendientes} />

      {turno ? (
        <>
          {reclamados > 0 && (
            <p className="mb-4 rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800">
              {`Se sumaron ${reclamados} cobro${reclamados === 1 ? '' : 's'} registrados antes de abrir.`}
            </p>
          )}
          <TurnoAbierto turno={turno} onCambio={onCambio} onError={onError} />
        </>
      ) : (
        <CajaCerrada
          onAbierta={(n) => {
            setReclamados(n)
            onCambio()
          }}
          onError={onError}
        />
      )}
    </>
  )
}

/* ── Pendientes de arqueo ─────────────────────────────────────────────────── */

function PendientesDeArqueo({ pendientes }: { pendientes: PendienteArqueo[] }) {
  if (pendientes.length === 0) return null

  const hoyLocal = diaLocal(new Date().toISOString())
  const conDiasAnteriores = pendientes.some((p) => diaLocal(p.fecha) !== hoyLocal)

  return (
    <section className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
      <h2 className="text-[13px] font-medium uppercase tracking-wide text-amber-800">
        Pendientes de arqueo
      </h2>
      <ul className="mt-2 divide-y divide-amber-100">
        {pendientes.map((p) => (
          <li key={p.id} className="flex items-baseline gap-3 py-2 text-sm">
            <span className="w-20 shrink-0 text-[13px] tabular-nums text-slate-500">
              {fechaCorta(p.fecha)}
            </span>
            <span className="min-w-0 flex-1 truncate font-medium text-slate-900">
              {p.clienteNombre}
            </span>
            <span className="txt-meta shrink-0">{ETIQUETA_MEDIO[p.medio] ?? p.medio}</span>
            <span className="shrink-0 font-medium tabular-nums text-slate-900">
              {guaranies(p.monto)}
            </span>
          </li>
        ))}
      </ul>
      {conDiasAnteriores && (
        <p className="mt-2 text-[13px] text-amber-800">
          De un día anterior: revisalo antes de cerrar, no se suma solo.
        </p>
      )}
    </section>
  )
}

/* ── Caja cerrada ────────────────────────────────────────────────────────── */

function CajaCerrada({
  onAbierta,
  onError,
}: {
  /** Cuántos cobros pendientes de arqueo reclamó la apertura (T25). */
  onAbierta: (reclamados: number) => void
  onError: (m: string | null) => void
}) {
  const [monto, setMonto] = useState(0)
  const [guardando, setGuardando] = useState(false)

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center">
      <p className="text-[15px] font-medium text-slate-900">La caja está cerrada</p>
      {/* No tener la caja abierta ya no es un bloqueo: el cobro se registra y
          queda pendiente de arqueo (T25). La regla explicada donde se tropieza
          con ella, no en un manual. */}
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
        Mientras esté cerrada se cobra igual: la plata del mostrador queda pendiente de arqueo y
        se suma sola al turno cuando se abra la caja.
      </p>

      <form
        className="mt-6 flex flex-wrap items-end justify-center gap-3"
        onSubmit={async (ev) => {
          ev.preventDefault()
          setGuardando(true)
          onError(null)
          try {
            const res = await fetch(`${API}/turnos`, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ montoInicial: monto }),
            })
            if (!res.ok) {
              const cuerpo = (await res.json().catch(() => ({}))) as { mensaje?: string }
              onError(cuerpo.mensaje ?? 'No pudimos abrir la caja')
              return
            }
            const cuerpo = (await res.json()) as { id: string; reclamados: number }
            onAbierta(cuerpo.reclamados)
          } finally {
            setGuardando(false)
          }
        }}
      >
        <label className="w-44 text-left">
          <span className="txt-meta block">Con cuánto arranca</span>
          <input
            type="number"
            min={0}
            step={1}
            value={monto}
            onChange={(e) => setMonto(Number(e.target.value))}
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-right text-[15px] tabular-nums"
          />
        </label>
        <Boton variante="primary" type="submit" disabled={guardando}>
          Abrir caja
        </Boton>
      </form>
    </div>
  )
}

/* ── Turno abierto ───────────────────────────────────────────────────────── */

function TurnoAbierto({
  turno,
  onCambio,
  onError,
}: {
  turno: Turno
  onCambio: () => void
  onError: (m: string | null) => void
}) {
  const [cerrando, setCerrando] = useState(false)

  const otrosMedios = Object.entries(turno.porMedio).filter(
    ([medio, monto]) => medio !== 'efectivo' && monto !== 0,
  )

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* El número que importa: lo que tiene que haber en el cajón. */}
        <div className="rounded-xl border border-teal-200 bg-teal-50/60 px-5 py-4">
          <p className="txt-meta">Efectivo esperado en el cajón</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums text-teal-900">
            {guaranies(turno.esperado)}
          </p>
          <p className="mt-1 text-[13px] text-teal-800/70">
            Arrancó con {guaranies(turno.montoInicial)}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
          <p className="txt-meta">Cobrado por otros medios</p>
          {otrosMedios.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">
              Todavía nada. Lo que entra por tarjeta o transferencia pertenece al turno pero no
              al cajón.
            </p>
          ) : (
            <dl className="mt-2 space-y-1">
              {otrosMedios.map(([medio, monto]) => (
                <div key={medio} className="flex items-baseline justify-between gap-4 text-sm">
                  <dt className="text-slate-600">{ETIQUETA_MEDIO[medio] ?? medio}</dt>
                  <dd className="font-medium tabular-nums text-slate-900">{guaranies(monto)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      <Libro movimientos={turno.movimientos} />

      <MovimientoManual turnoId={turno.id} onListo={onCambio} onError={onError} />

      <div className="border-t border-slate-200 pt-4">
        {cerrando ? (
          <Cierre
            turno={turno}
            onCancelar={() => setCerrando(false)}
            onCerrado={() => {
              setCerrando(false)
              onCambio()
            }}
            onError={onError}
          />
        ) : (
          <Boton variante="primary" onClick={() => setCerrando(true)}>
            Cerrar caja
          </Boton>
        )}
      </div>
    </div>
  )
}

/* ── El libro ────────────────────────────────────────────────────────────── */

function Libro({ movimientos }: { movimientos: Movimiento[] }) {
  return (
    <div>
      <h2 className="mb-2 text-[15px] font-medium text-slate-900">Movimientos del turno</h2>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {movimientos.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-slate-500">
            Todavía no hay movimientos.
          </p>
        ) : (
          <ul>
            {movimientos.map((m) => (
              <li
                key={m.id}
                className="flex items-baseline gap-3 border-b border-slate-100 px-4 py-2.5 last:border-b-0"
              >
                <span className="w-12 shrink-0 text-[13px] tabular-nums text-slate-400">
                  {hora(m.createdAt)}
                </span>
                <span className="w-20 shrink-0 text-[13px] text-slate-600">
                  {ETIQUETA_TIPO[m.tipo]}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                  {m.concepto ?? ETIQUETA_MEDIO[m.medio] ?? m.medio}
                </span>
                <span
                  className={`shrink-0 text-sm font-medium tabular-nums ${
                    m.monto < 0 ? 'text-rose-700' : 'text-slate-900'
                  }`}
                >
                  {guaranies(m.monto)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* La regla del libro, dicha donde se ve el libro. */}
      <p className="txt-meta mt-2">
        El libro no se edita: un error se corrige con otro movimiento, y los dos quedan.
      </p>
    </div>
  )
}

/* ── Retiros e ingresos a mano ───────────────────────────────────────────── */

function MovimientoManual({
  turnoId,
  onListo,
  onError,
}: {
  turnoId: string
  onListo: () => void
  onError: (m: string | null) => void
}) {
  const [abierto, setAbierto] = useState(false)
  const [tipo, setTipo] = useState<'retiro' | 'ingreso'>('retiro')
  const [monto, setMonto] = useState(0)
  const [concepto, setConcepto] = useState('')
  const [guardando, setGuardando] = useState(false)

  if (!abierto) {
    return (
      <Boton variante="secondary" onClick={() => setAbierto(true)}>
        Registrar retiro o ingreso
      </Boton>
    )
  }

  return (
    <form
      className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3"
      onSubmit={async (ev) => {
        ev.preventDefault()
        if (monto <= 0 || !concepto.trim()) return
        setGuardando(true)
        onError(null)
        try {
          const res = await fetch(`${API}/turnos/${turnoId}/movimientos`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              tipo,
              medio: 'efectivo',
              // Un retiro sale del cajón: va con signo negativo.
              monto: tipo === 'retiro' ? -monto : monto,
              concepto: concepto.trim(),
            }),
          })
          if (!res.ok) {
            const cuerpo = (await res.json().catch(() => ({}))) as { mensaje?: string }
            onError(cuerpo.mensaje ?? 'No pudimos registrar el movimiento')
            return
          }
          setAbierto(false)
          setMonto(0)
          setConcepto('')
          onListo()
        } finally {
          setGuardando(false)
        }
      }}
    >
      <label>
        <span className="txt-meta block">Qué es</span>
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value as 'retiro' | 'ingreso')}
          className="mt-1 h-9 rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
        >
          <option value="retiro">Sale del cajón</option>
          <option value="ingreso">Entra al cajón</option>
        </select>
      </label>

      <label className="w-32">
        <span className="txt-meta block">Monto</span>
        <input
          type="number"
          min={1}
          step={1}
          value={monto}
          onChange={(e) => setMonto(Number(e.target.value))}
          className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-right text-[14px] tabular-nums"
        />
      </label>

      <label className="min-w-0 flex-1">
        <span className="txt-meta block">Concepto</span>
        <input
          value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
          placeholder="Depósito al banco, compra de insumos…"
          className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-[14px]"
        />
      </label>

      <Boton variante="primary" type="submit" disabled={guardando || monto <= 0 || !concepto.trim()}>
        Registrar
      </Boton>
      <Boton variante="secondary" onClick={() => setAbierto(false)}>
        Cancelar
      </Boton>
    </form>
  )
}

/* ── Cierre ──────────────────────────────────────────────────────────────── */

function Cierre({
  turno,
  onCancelar,
  onCerrado,
  onError,
}: {
  turno: Turno
  onCancelar: () => void
  onCerrado: () => void
  onError: (m: string | null) => void
}) {
  const [declarado, setDeclarado] = useState(turno.esperado)
  const [guardando, setGuardando] = useState(false)

  const diferencia = declarado - turno.esperado

  return (
    <form
      className="rounded-xl border border-slate-200 bg-white px-5 py-4"
      onSubmit={async (ev) => {
        ev.preventDefault()
        setGuardando(true)
        onError(null)
        try {
          const res = await fetch(`${API}/turnos/${turno.id}/cerrar`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ montoDeclarado: declarado }),
          })
          if (!res.ok) {
            const cuerpo = (await res.json().catch(() => ({}))) as { mensaje?: string }
            onError(cuerpo.mensaje ?? 'No pudimos cerrar la caja')
            return
          }
          onCerrado()
        } finally {
          setGuardando(false)
        }
      }}
    >
      <p className="text-[15px] font-medium text-slate-900">Contá el cajón y cerrá</p>
      <p className="mt-1 text-sm text-slate-500">
        Lo que anotes acá es lo que había de verdad. La diferencia queda registrada tal cual:
        un faltante es información, no un error que haya que tapar.
      </p>
      {/* La frase que evita leer el cierre como una traba: la caja cierra, los
          cobros no. El arqueo sigue siendo sólo efectivo —lo de la tarjeta se
          lista, no se cuenta—, pero cerrar no congela la recepción. */}
      <p className="mt-1 text-sm text-slate-500">
        Cerrar la caja no impide cobrar: los cobros que entren después quedan pendientes para el
        próximo turno.
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-5">
        <label className="w-44">
          <span className="txt-meta block">Contado en el cajón</span>
          <input
            type="number"
            min={0}
            step={1}
            value={declarado}
            onChange={(e) => setDeclarado(Number(e.target.value))}
            autoFocus
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-right text-[15px] tabular-nums"
          />
        </label>

        <div>
          <span className="txt-meta block">Esperado</span>
          <p className="mt-1 flex h-10 items-center text-[15px] tabular-nums text-slate-700">
            {guaranies(turno.esperado)}
          </p>
        </div>

        <div>
          <span className="txt-meta block">Diferencia</span>
          <p
            className={`mt-1 flex h-10 items-center text-[15px] font-medium tabular-nums ${
              diferencia === 0
                ? 'text-slate-700'
                : diferencia < 0
                  ? 'text-rose-700'
                  : 'text-amber-700'
            }`}
          >
            {diferencia > 0 ? '+' : ''}
            {guaranies(diferencia)}
          </p>
        </div>
      </div>

      {diferencia !== 0 && (
        <p className="mt-3 text-[13px] text-slate-600">
          {diferencia < 0
            ? `Faltan ${guaranies(Math.abs(diferencia))} respecto de lo registrado.`
            : `Sobran ${guaranies(diferencia)} respecto de lo registrado.`}
        </p>
      )}

      <div className="mt-5 flex gap-2">
        <Boton variante="primary" type="submit" disabled={guardando}>
          Cerrar el turno
        </Boton>
        <Boton variante="secondary" onClick={onCancelar}>
          Todavía no
        </Boton>
      </div>
    </form>
  )
}
